<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="720px"
    :fullscreen="$vuetify.display.smAndDown"
  >
    <v-card elevation="0">
      <v-card-title class="text-wrap">
        {{ $t("waste.scanner.title") }}
      </v-card-title>
      <v-container>
        <v-row>
          <v-col cols="12">
            <qrcode-stream
              v-if="dialog"
              :paused="paused"
              class="waste-qr-stream"
              @decode="decode"
              @init="onInit"
            >
              <template #loading>
                <v-progress-circular indeterminate color="primary" />
              </template>
            </qrcode-stream>
          </v-col>

          <v-col v-if="messageKey" cols="12">
            <v-alert type="error" variant="tonal" border="bottom">
              {{ $t(messageKey) }}
            </v-alert>
          </v-col>

          <v-col cols="12">
            <div class="d-flex flex-wrap align-center ga-2">
              <v-btn variant="text" prepend-icon="mdi-refresh" @click="reset">
                {{ $t("waste.scanner.rescan") }}
              </v-btn>
              <v-spacer />
              <v-btn variant="outlined" color="error" @click="close">
                {{ $t("waste.scanner.close") }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
// Waste container QR scanner (Stage 6 slice 2). Copies the camera pattern of
// Workshop/workshopScanner.vue. The label holds the plain container id (D3): it is checked here
// (24 hex characters), the caller decides what to do with it. `check(id)` returns null to accept
// (the dialog closes and emits "scanned") or an i18n key to show and scan again.
// Emits scanned (id, lower case) and camera-unavailable.
import { QrcodeStream } from "vue3-qrcode-reader";
import { isContainerId } from "./wasteList.mjs";

export default {
  components: { QrcodeStream },
  props: {
    check: { type: Function, default: () => null },
  },
  emits: ["scanned", "camera-unavailable"],
  data: () => ({
    dialog: false,
    paused: false,
    messageKey: "",
  }),
  methods: {
    open() {
      this.reset();
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      this.reset();
    },
    reset() {
      this.paused = false;
      this.messageKey = "";
    },
    async onInit(promise) {
      try {
        await promise;
      } catch (err) {
        if (!this.dialog) return;
        this.close();
        this.$emit("camera-unavailable");
      }
    },
    decode(value) {
      if (!value) return;
      this.paused = true;
      const text = String(value).trim().toLowerCase();
      if (!isContainerId(text)) {
        this.messageKey = "waste.scanner.invalid_code";
        return;
      }
      const problem = this.check(text);
      if (problem) {
        this.messageKey = problem;
        return;
      }
      this.close();
      this.$emit("scanned", text);
    },
  },
};
</script>

<style scoped>
.waste-qr-stream {
  display: block;
  width: 100%;
  max-height: 55vh;
}

.waste-qr-stream :deep(video) {
  width: 100%;
  max-height: 55vh;
  object-fit: cover;
}

@media (max-width: 959.98px) {
  .waste-qr-stream,
  .waste-qr-stream :deep(video) {
    max-height: calc(100dvh - 300px);
  }
}
</style>
